import { Injectable } from "@angular/core";
import { LOG_CATEGORY, LOG_SUB_TYPE, LOG_TYPE, createAuditLogKey } from "@app/audit-log/services/audit-log.constants";
import { AuditLogService } from "@app/audit-log/services/audit-log.service";
import { Observable } from "rxjs";
import { catchError, exhaustMap, tap } from "rxjs/operators";
import { DataServiceError } from "../../shared/services/data-service-error";
import { AlulaDevice } from "../model/alula-device";
import { AlulaDealerService } from "./alula-dealer.service";
import { AlulaDeviceDbService } from "./alula-device-db.service";
import { AlulaUserService } from "./alula-user.service";

export { AlulaDealerService, AlulaUserService };

@Injectable({
    providedIn: "root",
})
export class AlulaService {
    enableSecurityMonitoring(payload): Observable<any> {
        return this.alulaDeviceService.upsertAlulaDevice(payload.device, payload.dealerId).pipe(
            tap(device => (payload.device = device)),
            exhaustMap(() => this.alulaUserService.upsertAlulaUser(payload)),
            tap(updatedUser => (payload.user = updatedUser)),
            exhaustMap(() => this.alulaDeviceService.assignDeviceToCustomer(payload)),
            exhaustMap(() => this.alulaDeviceService.updateAlulaDeviceProperties(payload.device)),
            tap(updatedDevice => (payload.device = updatedDevice)),
            tap(() => {
                this.logMonitoringActivateModify(payload);
            }),
            catchError(DataServiceError.handleError(payload))
        );
    }

    updateAlulaDevice(alulaDevice: AlulaDevice) {
        return this.alulaDeviceService.updateAlulaDeviceProperties(alulaDevice);
    }

    disableSecurityMonitoring(alulaDevice: AlulaDevice, payload?): Observable<any> {
        return this.alulaDeviceService.unassignDeviceFromCustomer(alulaDevice).pipe(
            exhaustMap(() => {
                this.alulaDeviceService.unregisterDevice(alulaDevice);
                return this.alulaDeviceService.getDeviceByMacAddress({
                    macAddress: alulaDevice.macAddress,
                });
            }),
            tap(_ => {
                const logKey = createAuditLogKey(LOG_CATEGORY.ACCOUNT, LOG_TYPE.CS_MONITORING, LOG_SUB_TYPE.DEACTIVATE);
                return this.logAuditEvt(logKey, {
                    data: `CS Name: ${payload?.csName}, CS Account Number: ${alulaDevice.csAccountNumber}`,
                    optionalParams: { uuid: payload?.uuid || "" },
                    entityId: payload.entityId,
                    projectId: payload.projectId,
                });
            }),
            catchError(DataServiceError.handleError(alulaDevice))
        );
    }

    private logMonitoringActivateModify(payload: any): void {
        const originalCsName = payload?.dataUpdate?.originalCs?.name
            ? `CS Name: ${payload.dataUpdate.originalCs.name}`
            : payload?.dataUpdate?.originalArn
            ? `CS Number: ${AlulaDevice.formatPhone(payload.dataUpdate.originalArn)}`
            : null;
        const originalCsAccountNumber = payload?.dataUpdate?.originalCsAccountNumber
            ? `CS Account Number: ${payload.dataUpdate.originalCsAccountNumber}`
            : null;
        const newCsName = payload?.dataUpdate?.cs?.name
            ? `CS Name: ${payload.dataUpdate.cs.name}`
            : `CS Number: ${AlulaDevice.formatPhone(payload?.dataUpdate?.arn)}`;
        const newCsAccountNumber = `CS Account Number: ${payload?.device?.csAccountNumber}`;

        const isActivate = payload?.dataUpdate?.isActivate;
        const isModify = payload?.dataUpdate?.isModify;
        let logData: string;
        let logKey: string;

        if (isActivate) {
            logKey = createAuditLogKey(LOG_CATEGORY.ACCOUNT, LOG_TYPE.CS_MONITORING, LOG_SUB_TYPE.ACTIVATE);
            logData = `${newCsName}, ${newCsAccountNumber}`;
        } else if (isModify) {
            logKey = createAuditLogKey(LOG_CATEGORY.ACCOUNT, LOG_TYPE.CS_MONITORING, LOG_SUB_TYPE.MODIFY);
            logData = `Original ${originalCsName}, ${originalCsAccountNumber}<br/>New ${newCsName}, ${newCsAccountNumber}`;
        }

        if (logKey && logData) {
            this.logAuditEvt(logKey, {
                data: logData,
                optionalParams: { uuid: payload?.project?.claresystem?.uuid },
                entityId: payload?.entityId,
                projectId: payload?.projectId,
            });
        }
    }

    private logAuditEvt(auditName: string, logData: any) {
        this.auditLogService.logEvent(auditName, logData);
    }
    constructor(
        private readonly alulaDeviceService: AlulaDeviceDbService,
        private readonly alulaUserService: AlulaUserService,
        private readonly auditLogService: AuditLogService
    ) {}
}
